import { Box, Checkbox, SpaceBetween } from "@amzn/awsui-components-console";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EnumeratedIconikAsset } from "@/components/iconik/types";

import useModal from "../useModal";
import styles from "./useClipMultiSelectModal.module.css";

export interface ClipMultiSelectConfig {
  /** Modal title. */
  title: string;
  /** One-line instruction shown above the list. */
  instruction: string;
  /** Confirm-button label. */
  confirmLabel: string;
  /** Called with the selected clips when the user confirms. */
  onConfirm: (clips: EnumeratedIconikAsset[]) => void;
  /**
   * Clips failing this predicate are hidden from the list and never selected
   * (e.g. only clips with a downloadable file are selectable for download).
   * Defaults to all clips selectable.
   */
  isSelectable?: (clip: EnumeratedIconikAsset) => boolean;
  /** Message shown when nothing is selectable. */
  emptyText?: string;
}

/**
 * Generic "pick which clips" modal used by the Clip Preview panel's
 * Download-Multiple and Add-to-Cart-Multiple flows. Presents the candidate
 * clips as a checkbox list (all selectable ones checked by default) and hands
 * the chosen subset to ``onConfirm``. The modal closes on confirm.
 */
const useClipMultiSelectModal = (config: ClipMultiSelectConfig) => {
  const { title, instruction, confirmLabel, onConfirm, isSelectable, emptyText } =
    config;

  const [candidates, setCandidates] = useState<EnumeratedIconikAsset[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const selectable = useMemo(
    () => candidates.filter((c) => (isSelectable ? isSelectable(c) : true)),
    [candidates, isSelectable]
  );

  const resolved = useMemo(
    () => selectable.filter((c) => selected.has(c.assetId)),
    [selectable, selected]
  );

  const toggleClip = (assetId: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(assetId);
      } else {
        next.delete(assetId);
      }
      return next;
    });

  const handleConfirm = useCallback(() => {
    if (resolved.length === 0) return;
    onConfirm(resolved);
  }, [resolved, onConfirm]);

  const content = (
    <SpaceBetween size="xs">
      <Box>{instruction}</Box>
      <div className={styles.clipList} data-testid="clip-multiselect-list">
        {selectable.length === 0 ? (
          <Box color="text-status-inactive">
            {emptyText ?? "No clips available."}
          </Box>
        ) : (
          selectable.map((clip) => (
            <label key={clip.assetId} className={styles.clipRow}>
              <Checkbox
                checked={selected.has(clip.assetId)}
                onChange={({ detail }) => toggleClip(clip.assetId, detail.checked)}
              />
              <span className={styles.clipName} title={clip.filename}>
                {clip.filename}
              </span>
            </label>
          ))
        )}
      </div>
    </SpaceBetween>
  );

  const { visible, toggle, ModalComponent } = useModal({
    title,
    onConfirm: handleConfirm,
    confirmLabel,
    disabled: resolved.length === 0,
    content,
  });

  const open = useCallback(
    (clips: EnumeratedIconikAsset[]) => {
      setCandidates(clips);
      setSelected(
        new Set(
          clips
            .filter((c) => (isSelectable ? isSelectable(c) : true))
            .map((c) => c.assetId)
        )
      );
      toggle();
    },
    [toggle, isSelectable]
  );

  useEffect(() => {
    if (!visible) {
      setCandidates([]);
      setSelected(new Set());
    }
  }, [visible]);

  return { ModalComponent, open };
};

export default useClipMultiSelectModal;
